import React, { useState } from 'react';

/**
 * RejectReportModal - Modal for sending a report back for revision
 * Based on approval.md UI/UX specifications
 */
const RejectReportModal = ({ isOpen, onClose, onReject, loading = false }) => {
  const [reason, setReason] = useState('');
  const [comments, setComments] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!reason.trim()) {
      setError('Please provide a reason for sending back this report');
      return;
    }

    try {
      await onReject(reason.trim(), comments.trim());
      setReason('');
      setComments('');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to send report back for revision');
    }
  };

  const handleClose = () => {
    setReason('');
    setComments('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} />

      {/* Modal */}
      <div className="flex min-h-full items-end justify-center p-3 text-center sm:items-center sm:p-0">
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-full sm:my-8 sm:w-full sm:max-w-xl">
          <form onSubmit={handleSubmit} className="flex flex-col max-h-[calc(100dvh-1.5rem)] sm:block sm:max-h-none">
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4 overflow-y-auto sm:overflow-visible">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 sm:mx-0 sm:h-10 sm:w-10">
                  <span className="text-amber-600 text-xl">↩</span>
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-grow">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900" id="modal-title">
                    Send Back for Revision
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This report will be sent back to the admin for corrections. The version number will be incremented when resubmitted.
                  </p>
                  <div className="mt-4 text-left">
                    {error && (
                      <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                        {error}
                      </div>
                    )}

                    <div className="mb-4">
                      <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
                        Reason for Revision <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="reason"
                        rows="5"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-y"
                        placeholder="Please provide detailed explanation of what needs to be revised or corrected..."
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        required
                        disabled={loading}
                      />
                      <p className="mt-1 text-xs text-gray-500">Be specific about what changes are required</p>
                    </div>

                    <div>
                      <label htmlFor="comments" className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Comments (Optional)
                      </label>
                      <textarea
                        id="comments"
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent resize-y"
                        placeholder="Any additional notes or suggestions for the admin..."
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-2">
              <button
                type="submit"
                disabled={!reason.trim() || loading}
                className="inline-flex w-full min-h-[44px] sm:min-h-0 items-center sm:items-stretch justify-center rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Back for Revision'
                )}
              </button>
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="mt-3 inline-flex w-full min-h-[44px] sm:min-h-0 items-center sm:items-stretch justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RejectReportModal;
